/**
 * Loading the corpus description, and knowing what is reachable.
 *
 * The manifest is small (a few hundred KB for the whole archive) and is the only thing
 * that must be fetched before the app is usable, so it is cached aggressively and the
 * app runs from the cached copy while revalidating.
 */

import { signal } from '@preact/signals';
import type { Cadence, Manifest, Recording } from '../types';
import { isEligible } from '../session/eligibility';
import { prepareCompositionCatalog, queryCompositionCatalog } from '../session/composition-catalog';
import { assetOrigin, isNativeShell } from '../config/origin';
import {
  ManifestValidationError, parseManifest, parseReleaseDescriptor,
} from '../composition/manifest';

export const manifest = signal<Manifest | null>(null);
export const manifestError = signal<string | null>(null);
export const loading = signal(true);

const CACHE = 'brahm-manifest-v1';
// relative-url-ok: this is both the Cache API key and the shell's offline fallback, where
// the bundled copy is the point. The live fetch below prefixes it with assetOrigin().
const URL_PATH = '/manifest.json';
// relative-url-ok: only a route suffix; requests prefix it with the platform's assetOrigin().
const RELEASE_PATH = '/audio/release.json';

class ManifestFetchError extends Error {}
class ManifestNotFoundError extends ManifestFetchError {}

async function response(url: string): Promise<Response> {
  let res: Response;
  try { res = await fetch(url, { cache: 'no-cache' }); }
  catch (error) { throw new ManifestFetchError((error as Error).message); }
  if (res.status === 404) throw new ManifestNotFoundError('404');
  if (!res.ok) throw new ManifestFetchError(`${res.status}`);
  return res;
}

async function fetchManifest(url: string): Promise<Manifest> {
  const res = await response(url);
  let body: unknown;
  try { body = await res.json(); }
  catch { throw new ManifestValidationError('response is not valid JSON'); }
  return parseManifest(body);
}

async function sha256(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function fetchRelease(origin: string): Promise<Manifest | null> {
  let pointerResponse: Response;
  try { pointerResponse = await response(`${origin}${RELEASE_PATH}`); }
  catch (error) {
    if (error instanceof ManifestNotFoundError) return null;
    throw error;
  }
  let pointerBody: unknown;
  try { pointerBody = await pointerResponse.json(); }
  catch { throw new ManifestValidationError('release pointer is not valid JSON'); }
  const release = parseReleaseDescriptor(pointerBody);
  let immutable: Response;
  try { immutable = await response(`${origin}/audio/${release.manifest.path}`); }
  catch (error) {
    throw new ManifestValidationError(`referenced release manifest is unavailable (${(error as Error).message})`);
  }
  const bytes = new Uint8Array(await immutable.arrayBuffer());
  if (bytes.byteLength !== release.manifest.bytes) {
    throw new ManifestValidationError(
      `release manifest byte length ${bytes.byteLength} does not match ${release.manifest.bytes}`,
    );
  }
  const actual = `sha256:${await sha256(bytes)}`;
  if (actual !== release.manifest.contentDigest) {
    throw new ManifestValidationError(`release manifest digest ${actual} does not match pointer`);
  }
  let body: unknown;
  try { body = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes)); }
  catch { throw new ManifestValidationError('release manifest is not valid UTF-8 JSON'); }
  const parsed = parseManifest(body);
  return {
    ...parsed,
    releaseDigest: release.releaseDigest,
    manifestDigest: release.manifestDigest,
    sourceManifestDigest: release.sourceManifestDigest,
    releaseManifest: release.manifest.path,
  };
}

async function fetchCurrent(origin: string): Promise<Manifest> {
  const released = await fetchRelease(origin);
  return released ?? fetchManifest(`${origin}${URL_PATH}`);
}

export async function loadManifest(): Promise<void> {
  loading.value = true;
  manifestError.value = null;

  // Serve from cache immediately if we have it, so an offline start is instant.
  let hasValidCache = false;
  try {
    const cached = await (await caches.open(CACHE)).match(URL_PATH);
    if (cached) {
      const parsed = parseManifest(await cached.json());
      await prepareCompositionCatalog(parsed);
      manifest.value = parsed;
      hasValidCache = true;
    }
  } catch { /* no Cache API, or private mode */ }

  try {
    // The native shell must read the manifest from the same place the audio comes from.
    // Its own bundle has a copy — the web build is packaged into the APK — but that copy is
    // frozen at build time, and `generatedAt` is the cache identity the disk cache uses to
    // tell one cut of a recording from another. A frozen manifest against live R2 bytes is
    // the re-cut trap again: a part whose boundary moved but whose length did not would be
    // played against the previous cut's frame counts. The bundled copy is the offline
    // fallback below, never the first answer.
    let fresh: Manifest;
    try {
      fresh = await fetchCurrent(assetOrigin());
    } catch (error) {
      // A release already cached on the phone is the exact manifest for its immutable
      // assets. The APK copy is older by construction and must not replace it offline.
      if (isNativeShell() && error instanceof ManifestFetchError && !hasValidCache) {
        fresh = await fetchManifest(URL_PATH);
      } else {
        throw error;
      }
    }
    await prepareCompositionCatalog(fresh);
    manifest.value = fresh;
    try { await (await caches.open(CACHE)).put(URL_PATH, new Response(JSON.stringify(fresh))); }
    catch { /* best effort */ }
  } catch (e) {
    if (!manifest.value || !(e instanceof ManifestFetchError)) {
      manifestError.value = `Could not load the library (${(e as Error).message}).`;
    }
    // A stale manifest is fine offline; the audio it points at is immutable.
  } finally {
    loading.value = false;
  }
}

/**
 * Which recordings can actually be this long.
 *
 * Duration filters the corpus rather than being applied after a choice is made,
 * because only 54% of recordings can reach 20 minutes — offering a length and then
 * finding nothing is the obvious way for this product to feel broken.
 */
export function reachable(
  targetSec: number, cadence: Cadence, toleranceSec?: number, desiredSpeechRatio?: number | null,
): Recording[] {
  const m = manifest.value;
  if (!m) return [];
  const indexed = queryCompositionCatalog(m,targetSec,cadence,toleranceSec,desiredSpeechRatio ?? undefined);
  return m.recordings.filter((recording) => indexed ? indexed.has(recording.id) : isEligible({
    recording, targetSec, cadence, toleranceSec,
    desiredSpeechRatio: desiredSpeechRatio ?? undefined,
  }));
}

/** Durations we can offer at all, for greying out the impossible ones. */
export function reachableDurations(
  cadence: Cadence, candidates: number[], toleranceSec?: number, desiredSpeechRatio?: number | null,
): Set<number> {
  const m = manifest.value;
  const out = new Set<number>();
  if (!m) return out;
  for (const sec of candidates) {
    if (m.recordings.some((recording) => isEligible({
      recording, targetSec: sec, cadence, toleranceSec,
      desiredSpeechRatio: desiredSpeechRatio ?? undefined,
    }))) out.add(sec);
  }
  return out;
}

export function recordingById(id: string): Recording | undefined {
  return manifest.value?.recordings.find((r) => r.id === id);
}
